import { z } from "zod";

/**
 * Saved insights prompts — backs the saved-prompts drawer next to the
 * Insights & Recommendations composer.
 *
 * Authoring model (post-`/insights/run` revamp): there is no longer a
 * "Save this prompt" button. Whenever the user runs `/insights/run`
 * with a non-empty `prompt` and a `user_id`, the backend auto-dedupes
 * by exact text and persists a row here. The drawer is purely a
 * "history of things I asked" surface — clicking a row populates the
 * textarea so the user can re-run or tweak.
 *
 * `display_label` is server-computed (~50 chars, summarises `prompt`)
 * and is what the drawer list should render. `prompt` is the full text
 * and goes back into the textarea.
 */
export const SavedPromptSchema = z.object({
  /** uuid4 hex — opaque, safe for React keys. */
  prompt_id: z.string(),
  user_id: z.string(),
  /** Full user-authored text. Round-tripped into the textarea on click. */
  prompt: z.string(),
  /** Server-computed short label (~50 chars). Render this in the list. */
  display_label: z.string(),
  /** Monotonic per prompt; increments on PUT. Useful for client-side cache busting. */
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SavedPrompt = z.infer<typeof SavedPromptSchema>;

export const SavedPromptListSchema = z.array(SavedPromptSchema);

/**
 * Body shared by POST (manual create — rare; auto-save covers most cases)
 * and PUT (rename/edit).
 */
export interface SavedPromptInput {
  /** Required. Soft-capped at `PROMPT_MAX`. */
  prompt: string;
  /** Free-form audit string — defaults to the user's id/email server-side. */
  updated_by?: string;
}

/**
 * Hard cap shared with `/insights/run`. Mirrored from
 * `schema/brand-os/insights-run.ts` so the prompts module can be imported
 * without pulling in the full insights-run barrel.
 */
export const PROMPT_MAX = 4000;
